import type { ReactNode } from 'react';
import { Button, Group, Skeleton, Stack, Table, Text } from '@mantine/core';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';

export interface DataTableColumn<T> {
  key: string;
  label: string;
  accessor: (row: T) => ReactNode;
  rawValue?: (row: T) => string | number;
  summaryAccessor?: (summary: Record<string, unknown>) => ReactNode;
  align?: 'left' | 'right';
  sortable?: boolean;
  /**
   * Fixed column width (px). Use for narrow, non-negotiable columns — chiefly a
   * trailing action-menu column, which must keep its full width rather than being
   * the column that absorbs the squeeze at narrow viewports (QHP-0039).
   */
  width?: number;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  keyField?: keyof T;
  summary?: Record<string, unknown>;
  summaryLabel?: string;
  loading?: boolean;
  page?: number;
  totalPages?: number;
  totalCount?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string) => void;
  onPageChange?: (page: number) => void;
  clientSort?: boolean;
  emptyMessage?: string;
  minWidth?: number;
  highlightCell?: (row: T, columnKey: string) => {
    color?: string;
    fontWeight?: number;
  } | undefined;
  onRowClick?: (row: T) => void;
}

function SortIcon({
  column,
  sortBy,
  sortDirection,
}: {
  column: string;
  sortBy?: string;
  sortDirection?: string;
}) {
  if (sortBy !== column) return <ArrowUpDown size={12} />;
  return sortDirection === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
}

export function DataTable<T>({
  columns,
  data,
  keyField,
  summary,
  summaryLabel,
  loading = false,
  page = 1,
  totalPages = 0,
  totalCount = 0,
  sortBy,
  sortDirection,
  onSort,
  onPageChange,
  clientSort = false,
  emptyMessage = 'No records found. Adjust filters and try again.',
  minWidth = 800,
  highlightCell,
  onRowClick,
}: DataTableProps<T>) {
  const sortedData = (() => {
    if (!clientSort || !sortBy) return data;
    const col = columns.find((c) => c.key === sortBy);
    if (!col?.sortable) return data;
    const getValue = col.rawValue ?? ((row: T) => (row as Record<string, unknown>)[col.key]);
    return [...data].sort((a, b) => {
      const aVal = getValue(a);
      const bVal = getValue(b);
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp = typeof aVal === 'number' && typeof bVal === 'number'
        ? aVal - bVal
        : String(aVal).localeCompare(String(bVal));
      return sortDirection === 'desc' ? -cmp : cmp;
    });
  })();

  if (loading) {
    return (
      <Stack gap="xs" p="md">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} height={36} />
        ))}
      </Stack>
    );
  }

  if (sortedData.length === 0) {
    return (
      <Text c="dimmed" ta="center" py="xl">
        {emptyMessage}
      </Text>
    );
  }

  const getKey = (row: T, index: number): string => {
    if (keyField && row[keyField] != null) return String(row[keyField]);
    return String(index);
  };

  return (
    <Stack gap="sm">
      <div style={{ overflowX: 'auto' }}>
        <Table
          striped
          highlightOnHover
          withTableBorder
          withColumnBorders
          style={{ minWidth }}
        >
          <Table.Thead>
            <Table.Tr>
              {columns.map((col) => (
                <Table.Th
                  key={col.key}
                  style={{
                    textAlign: col.align ?? 'left',
                    whiteSpace: 'nowrap',
                    cursor: col.sortable && onSort ? 'pointer' : undefined,
                    userSelect: 'none',
                    width: col.width,
                    minWidth: col.width,
                  }}
                  onClick={col.sortable && onSort ? () => onSort(col.key) : undefined}
                >
                  <Group
                    gap={4}
                    justify={col.align === 'right' ? 'flex-end' : 'flex-start'}
                    wrap="nowrap"
                  >
                    {col.label}
                    {col.sortable && onSort && (
                      <SortIcon column={col.key} sortBy={sortBy} sortDirection={sortDirection} />
                    )}
                  </Group>
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedData.map((row, index) => (
              <Table.Tr
                key={getKey(row, index)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={{ cursor: onRowClick ? 'pointer' : undefined }}
              >
                {columns.map((col) => {
                  const highlight = highlightCell?.(row, col.key);
                  return (
                    <Table.Td
                      key={col.key}
                      style={{
                        textAlign: col.align ?? 'left',
                        whiteSpace: 'nowrap',
                        color: highlight?.color,
                        fontWeight: highlight?.fontWeight,
                        width: col.width,
                        minWidth: col.width,
                      }}
                    >
                      {col.accessor(row)}
                    </Table.Td>
                  );
                })}
              </Table.Tr>
            ))}
          </Table.Tbody>
          {summary && (
            <Table.Tfoot>
              <Table.Tr
                style={{
                  fontWeight: 700,
                  backgroundColor: 'var(--mantine-color-gray-1)',
                }}
              >
                {columns.map((col, index) => (
                  <Table.Td
                    key={col.key}
                    style={{
                      textAlign: col.align ?? 'left',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {index === 0 && summaryLabel
                      ? summaryLabel
                      : col.summaryAccessor
                        ? col.summaryAccessor(summary)
                        : ''}
                  </Table.Td>
                ))}
              </Table.Tr>
            </Table.Tfoot>
          )}
        </Table>
      </div>

      {totalPages > 0 && onPageChange && (
        <Group justify="space-between" align="center" wrap="wrap">
          <Text size="sm" c="dimmed">
            Showing {sortedData.length} of {totalCount} records — Page {page} of {totalPages}
          </Text>
          <Group gap="xs">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              Next
            </Button>
          </Group>
        </Group>
      )}
    </Stack>
  );
}
